"use client"

import type { CSSProperties } from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useGSAP } from "@gsap/react"
import cn from "classnames"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { useScroll } from "~/components/foundations/ScrollProvider"
import { Asset } from "~/components/primitives/Asset/Asset"
import { Button } from "~/components/primitives/Button/Button"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { Icon } from "~/components/primitives/icon"
import { useMatchMedia } from "~/hooks/useMatchMedia"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { VideoCard, VideoCardsInterface } from "./VideoCards.interface"
import s from "./VideoCards.module.scss"

export type VideoCardsViewProps = Omit<VideoCardsInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
  initialExpandedCardId?: string | null
}

gsap.registerPlugin(ScrollTrigger)

const getReducedMotionAsset = (card: VideoCard) => {
  return (
    card.posterImage ??
    (card.videoSource.kind === "video" ? card.videoSource : null)
  )
}

export const VideoCardsView = ({
  moduleIndex,
  cards,
  config,
  className,
  style,
  initialExpandedCardId = null,
}: VideoCardsViewProps) => {
  const configProps = getModuleConfigProps(config)
  const [expandedCardId, setExpandedCardId] = useState<string | null>(
    initialExpandedCardId,
  )

  const { lenis } = useScroll()
  const prefersReducedMotion = useMatchMedia("(prefers-reduced-motion: reduce)")

  const sectionRef = useRef<HTMLElement>(null)
  const cardRefs = useRef<Record<string, HTMLElement | null>>({})
  const overlayRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const cardContentRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const cardHeadingRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const mediaFrameRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const isSm = useMatchMedia("sm")

  useGSAP(
    () => {
      if (!lenis || !isSm) return

      const mm = gsap.matchMedia()

      mm.add(
        "(prefers-reduced-motion: no-preference) and (min-width: 768px)",
        () => {
          const animatedElements: HTMLElement[] = []

          cards.forEach((cardData) => {
            const card = cardRefs.current[cardData.id]
            const header = cardHeadingRefs.current[cardData.id]
            const overlay = overlayRefs.current[cardData.id]
            const mediaFrame = mediaFrameRefs.current[cardData.id]
            const cardContent = cardContentRefs.current[cardData.id]
            if (!card || !mediaFrame || !overlay || !cardContent) return

            animatedElements.push(cardContent)

            gsap.fromTo(
              cardContent,
              { scale: 0.9 },
              {
                scale: 1,
                ease: "none",
                scrollTrigger: {
                  trigger: mediaFrame,
                  endTrigger: overlay,
                  scroller: lenis.rootElement,
                  start: "center center",
                  end: "center center",
                  scrub: true,
                  pin: cardContent,
                  pinnedContainer: cardContent,
                  pinSpacing: false,
                },
              },
            )
            if (!header) return
            animatedElements.push(header)
            gsap.fromTo(
              header,
              { top: "50%", y: "-50%" },
              {
                top: "0%",
                y: "0%",
                ease: "none",
                scrollTrigger: {
                  trigger: mediaFrame,
                  scroller: lenis.rootElement,
                  start: "center bottom",
                  endTrigger: card,
                  end: "bottom bottom",
                  scrub: true,
                },
              },
            )
            if (!overlay) return
            animatedElements.push(overlay)
            gsap.fromTo(
              overlay,
              { rotate: -4, scale: 0.9 },
              {
                rotate: 0,
                scale: 1.015,
                ease: "none",
                scrollTrigger: {
                  trigger: overlay,
                  scroller: lenis.rootElement,
                  start: "top bottom",
                  end: "center center",
                  scrub: true,
                },
              },
            )
          })

          return () => {
            gsap.set(animatedElements, { clearProps: "all" })
          }
        },
      )

      return () => mm.revert()
    },
    {
      scope: sectionRef,
      dependencies: [lenis, isSm, cards],
      revertOnUpdate: true,
    },
  )

  useEffect(() => {
    if (!expandedCardId) return

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setExpandedCardId(null)
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [expandedCardId])

  const toggleCard = useCallback((cardId: string) => {
    setExpandedCardId((currentCardId) =>
      currentCardId === cardId ? null : cardId,
    )
  }, [])

  const closeCard = useCallback(() => {
    setExpandedCardId(null)
  }, [])

  return (
    <section
      ref={sectionRef}
      className={cn(s.videoCards, `stack-module-${moduleIndex}`, className)}
      aria-label="Video cards"
      data-module="video-cards"
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className={cn("container", "default-grid")}>
        <div className={s.cardList}>
          {cards.map((card, index) => {
            const titleId = `video-card-${card.id}-title`
            const overlayId = `video-card-${card.id}-overlay`
            const isExpanded = expandedCardId === card.id
            const reducedMotionAsset = getReducedMotionAsset(card)

            return (
              <article
                key={card.id}
                className={s.card}
                aria-labelledby={titleId}
                data-card-id={card.id}
                data-card-index={index + 1}
                data-expanded={isExpanded}
                ref={(element) => {
                  cardRefs.current[card.id] = element
                }}
              >
                <div className={s.wrapper}>
                  <div
                    className={s.cardContent}
                    ref={(element) => {
                      cardContentRefs.current[card.id] = element
                    }}
                  >
                    <div
                      className={s.cardHeading}
                      ref={(element) => {
                        cardHeadingRefs.current[card.id] = element
                      }}
                    >
                      <FadeIn delay={0.1}>
                        <AnimateText
                          id={titleId}
                          element="h2"
                          className={s.title}
                        >
                          <HtmlRenderer inline html={card.entityTitleHtml} />
                        </AnimateText>
                      </FadeIn>

                      {card.cta && (
                        <FadeIn delay={0.2}>
                          <Button
                            cta={{ ...card.cta, variant: "tertiary" }}
                            theme={configProps["data-typography"] || "light"}
                            className={s.cta}
                          />
                        </FadeIn>
                      )}
                    </div>
                    <div
                      className={s.mediaFrame}
                      ref={(element) => {
                        mediaFrameRefs.current[card.id] = element
                      }}
                    >
                      <div
                        className={s.backdrop}
                        onClick={closeCard}
                        aria-hidden="true"
                        data-expanded={isExpanded}
                      />

                      <div
                        id={overlayId}
                        className={cn(s.infoCard, s.mobile)}
                        data-expanded={isExpanded}
                      >
                        <button
                          type="button"
                          className={s.closeButton}
                          aria-label="Close information overlay"
                          onClick={closeCard}
                        >
                          <Icon
                            id="accordion-toggle"
                            className={s.closeIcon}
                            aria-hidden="true"
                          />
                        </button>

                        <HtmlRenderer
                          html={card.overlayBodyHtml}
                          className={s.overlayBody}
                          element="div"
                        />
                      </div>
                      <p className={s.shortDescription}>
                        {card.shortDescription}
                      </p>
                      <div className={s.mediaAssetWrapper}>
                        {prefersReducedMotion && reducedMotionAsset ? (
                          <Asset
                            asset={reducedMotionAsset}
                            className={s.mediaAsset}
                            objectFit="cover"
                            imageSizes={{ base: "100vw", md: "50vw" }}
                            isVideoPlaying={false}
                          />
                        ) : (
                          <Asset
                            asset={card.videoSource}
                            className={s.mediaAsset}
                            objectFit="cover"
                            imageSizes={{ base: "100vw", md: "50vw" }}
                            aria-hidden="true"
                            loading="eager"
                            videoProps={{
                              muted: true,
                              loop: true,
                              playsInline: true,
                            }}
                          />
                        )}
                      </div>
                      <button
                        type="button"
                        className={s.expandButton}
                        aria-expanded={isExpanded}
                        aria-controls={overlayId}
                        aria-label={`Read more about ${card.entityTitleText}`}
                        onClick={() => toggleCard(card.id)}
                      >
                        <Icon
                          id="accordion-toggle"
                          className={s.toggleIcon}
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>

                  <div
                    id={overlayId}
                    ref={(element) => {
                      overlayRefs.current[card.id] = element
                    }}
                    className={s.infoCard}
                    data-expanded={isExpanded}
                  >
                    <HtmlRenderer
                      html={card.overlayBodyHtml}
                      className={s.overlayBody}
                      element="div"
                    />
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      </div>
    </section>
  )
}
